import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit2, X, ChevronUp, ChevronDown } from 'lucide-react';
import { supabase } from '../../lib/db.ts';
import { logger } from '../../services/logger.ts';
import { useAuth } from '../../lib/auth.tsx';
import { Announcement } from '../../hooks/useAnnouncements.ts';
import { getGradientOptions, GradientPreset } from '../../config/announcementGradients.ts';

export function AnnouncementsManager() {
  const { user } = useAuth();
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [isEditing, setIsEditing] = useState<string | null>(null);
  const [newAnnouncement, setNewAnnouncement] = useState({ 
    title: '', 
    message: '', 
    is_active: true,
    display_order: 0,
    expires_at: '',
    gradient_color: 'default' as GradientPreset
  });
  const [isAdding, setIsAdding] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data, error: fetchError } = await supabase
        .from('announcements')
        .select('*')
        .order('display_order', { ascending: true })
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setAnnouncements(data || []);
    } catch (err) {
      logger.error('Error fetching announcements:', err);
      setError('Failed to load announcements');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAnnouncement = async () => {
    try {
      if (!newAnnouncement.title.trim() || !newAnnouncement.message.trim()) {
        setError('Title and message are required');
        return;
      }

      setError(null);
      const expiresAt = newAnnouncement.expires_at 
        ? new Date(newAnnouncement.expires_at).toISOString()
        : null;

      const { error: insertError } = await supabase
        .from('announcements')
        .insert({
          title: newAnnouncement.title.trim(),
          message: newAnnouncement.message.trim(),
          is_active: newAnnouncement.is_active,
          display_order: newAnnouncement.display_order || 0,
          expires_at: expiresAt,
          gradient_color: newAnnouncement.gradient_color || 'default',
          created_by: user?.id
        });

      if (insertError) throw insertError;

      setSuccess('Announcement added successfully');
      await fetchAnnouncements();
      setNewAnnouncement({ 
        title: '', 
        message: '', 
        is_active: true,
        display_order: 0,
        expires_at: '',
        gradient_color: 'default' as GradientPreset
      });
      setIsAdding(false);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      logger.error('Error adding announcement:', err);
      setError(err instanceof Error ? err.message : 'Failed to add announcement');
    }
  };

  const handleUpdateAnnouncement = async (id: string, announcement: Partial<Announcement>) => {
    try {
      setError(null);
      const updateData: {
        title?: string;
        message?: string;
        is_active?: boolean;
        display_order?: number;
        gradient_color?: string;
        expires_at?: string | null;
      } = {
        title: announcement.title?.trim(),
        message: announcement.message?.trim(),
        is_active: announcement.is_active,
        display_order: announcement.display_order,
        gradient_color: announcement.gradient_color || 'default'
      };

      if (announcement.expires_at !== undefined) {
        updateData.expires_at = announcement.expires_at 
          ? new Date(announcement.expires_at).toISOString()
          : null;
      }

      const { error: updateError } = await supabase
        .from('announcements')
        .update(updateData)
        .eq('id', id);

      if (updateError) throw updateError;

      setSuccess('Announcement updated successfully');
      await fetchAnnouncements();
      setIsEditing(null);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      logger.error('Error updating announcement:', err);
      setError(err instanceof Error ? err.message : 'Failed to update announcement');
    }
  };

  const handleDeleteAnnouncement = async (id: string) => {
    if (!confirm('Are you sure you want to delete this announcement?')) {
      return;
    }

    try {
      setError(null);
      const { error: deleteError } = await supabase
        .from('announcements')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;

      setSuccess('Announcement deleted successfully');
      await fetchAnnouncements();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      logger.error('Error deleting announcement:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete announcement');
    }
  };

  const handleReorder = async (id: string, newOrder: number) => {
    try {
      setError(null);
      const { error: updateError } = await supabase
        .from('announcements')
        .update({ display_order: newOrder })
        .eq('id', id);

      if (updateError) throw updateError;

      await fetchAnnouncements();
    } catch (err) {
      logger.error('Error reordering announcement:', err);
      setError(err instanceof Error ? err.message : 'Failed to reorder announcement');
    }
  };

  const handleToggleVisibility = async (id: string, currentStatus: boolean) => {
    try {
      setError(null);
      const { error: updateError } = await supabase
        .from('announcements')
        .update({ is_active: !currentStatus })
        .eq('id', id);

      if (updateError) throw updateError;

      setSuccess(`Announcement ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
      await fetchAnnouncements();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      logger.error('Error toggling announcement visibility:', err);
      setError(err instanceof Error ? err.message : 'Failed to toggle visibility');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Manage Announcements</h2>
        <button
          type="button"
          onClick={() => setIsAdding(true)}
          className="flex items-center gap-2 px-4 py-2 bg-wayja-teal text-white rounded-md hover:bg-wayja-teal/90 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Add Announcement
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-md">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded-md">
          {success}
        </div>
      )}

      {isAdding && (
        <div className="mb-6 p-4 border border-gray-200 rounded-lg">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Add New Announcement</h3>
            <button
              type="button"
              onClick={() => {
                setIsAdding(false);
                setNewAnnouncement({ 
                  title: '', 
                  message: '', 
                  is_active: true,
                  display_order: 0,
                  expires_at: '',
                  gradient_color: 'default' as GradientPreset
                });
                setError(null);
              }}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title *
              </label>
              <input
                type="text"
                value={newAnnouncement.title}
                onChange={(e) => setNewAnnouncement({ ...newAnnouncement, title: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-wayja-teal"
                placeholder="Earn Market Commissions"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Message *
              </label>
              <textarea
                value={newAnnouncement.message}
                onChange={(e) => setNewAnnouncement({ ...newAnnouncement, message: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-wayja-teal"
                rows={4}
                placeholder="📈💰Not seeing the market you're looking for? Create one and earn 1% commission off every trade made in that market! 💰📈"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Background Gradient
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {getGradientOptions().map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setNewAnnouncement({ ...newAnnouncement, gradient_color: option.value as GradientPreset })}
                    className={`relative p-3 rounded-lg border-2 transition-all ${
                      newAnnouncement.gradient_color === option.value
                        ? 'border-wayja-teal ring-2 ring-wayja-teal ring-opacity-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                    style={{
                      background: `linear-gradient(to right, ${option.colors.join(', ')})`
                    }}
                  >
                    <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-10 rounded-lg transition-all" />
                    <div className="relative text-white text-xs font-medium text-center">
                      {option.label}
                    </div>
                  </button>
                ))}
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Selected: {getGradientOptions().find(o => o.value === newAnnouncement.gradient_color)?.description}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Display Order
                </label>
                <input
                  type="number"
                  value={newAnnouncement.display_order}
                  onChange={(e) => setNewAnnouncement({ ...newAnnouncement, display_order: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-wayja-teal"
                  min="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expires At (Optional)
                </label>
                <input
                  type="datetime-local"
                  value={newAnnouncement.expires_at}
                  onChange={(e) => setNewAnnouncement({ ...newAnnouncement, expires_at: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-wayja-teal"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="new-active"
                checked={newAnnouncement.is_active}
                onChange={(e) => setNewAnnouncement({ ...newAnnouncement, is_active: e.target.checked })}
                className="w-4 h-4 text-wayja-teal focus:ring-wayja-teal border-gray-300 rounded"
              />
              <label htmlFor="new-active" className="text-sm font-medium text-gray-700">
                Active
              </label>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setIsAdding(false);
                  setNewAnnouncement({ 
                    title: '', 
                    message: '', 
                    is_active: true,
                    display_order: 0,
                    expires_at: '',
                    gradient_color: 'default' as GradientPreset
                  });
                  setError(null);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAddAnnouncement}
                className="px-4 py-2 bg-wayja-teal text-white rounded-md hover:bg-wayja-teal/90 transition-colors"
              >
                Save Announcement
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {announcements.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No announcements yet. Click "Add Announcement" to create one.
          </div>
        ) : (
          announcements.map((announcement, index) => (
            <div
              key={announcement.id}
              className="p-4 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors"
            >
              {isEditing === announcement.id ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Title *
                    </label>
                    <input
                      type="text"
                      value={announcement.title}
                      onChange={(e) => {
                        setAnnouncements(announcements.map(a => 
                          a.id === announcement.id ? { ...a, title: e.target.value } : a
                        ));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-wayja-teal"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Message *
                    </label>
                    <textarea
                      value={announcement.message}
                      onChange={(e) => {
                        setAnnouncements(announcements.map(a => 
                          a.id === announcement.id ? { ...a, message: e.target.value } : a
                        ));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-wayja-teal"
                      rows={4}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Background Gradient
                    </label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {getGradientOptions().map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => {
                            setAnnouncements(announcements.map(a => 
                              a.id === announcement.id ? { ...a, gradient_color: option.value } : a
                            ));
                          }}
                          className={`relative p-3 rounded-lg border-2 transition-all ${
                            (announcement.gradient_color || 'default') === option.value
                              ? 'border-wayja-teal ring-2 ring-wayja-teal ring-opacity-50'
                              : 'border-gray-300 hover:border-gray-400'
                          }`}
                          style={{
                            background: `linear-gradient(to right, ${option.colors.join(', ')})`
                          }}
                        >
                          <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-10 rounded-lg transition-all" />
                          <div className="relative text-white text-xs font-medium text-center">
                            {option.label}
                          </div>
                        </button>
                      ))}
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      Selected: {getGradientOptions().find(o => o.value === (announcement.gradient_color || 'default'))?.description}
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Display Order
                      </label>
                      <input
                        type="number"
                        value={announcement.display_order}
                        onChange={(e) => {
                          setAnnouncements(announcements.map(a => 
                            a.id === announcement.id ? { ...a, display_order: parseInt(e.target.value) || 0 } : a
                          ));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-wayja-teal"
                        min="0"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Expires At (Optional)
                      </label>
                      <input
                        type="datetime-local"
                        value={announcement.expires_at ? new Date(announcement.expires_at).toISOString().slice(0, 16) : ''}
                        onChange={(e) => {
                          setAnnouncements(announcements.map(a => 
                            a.id === announcement.id ? { ...a, expires_at: e.target.value || null } : a
                          ));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-wayja-teal"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id={`active-${announcement.id}`}
                      checked={announcement.is_active}
                      onChange={(e) => {
                        setAnnouncements(announcements.map(a => 
                          a.id === announcement.id ? { ...a, is_active: e.target.checked } : a
                        ));
                      }}
                      className="w-4 h-4 text-wayja-teal focus:ring-wayja-teal border-gray-300 rounded"
                    />
                    <label htmlFor={`active-${announcement.id}`} className="text-sm font-medium text-gray-700">
                      Active
                    </label>
                  </div>
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setIsEditing(null)}
                      className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => handleUpdateAnnouncement(announcement.id, announcement)}
                      className="px-4 py-2 bg-wayja-teal text-white rounded-md hover:bg-wayja-teal/90 transition-colors"
                    >
                      Save Changes
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-lg font-semibold text-gray-900">{announcement.title}</h3>
                        <button
                          type="button"
                          onClick={() => handleToggleVisibility(announcement.id, announcement.is_active)}
                          className={`px-2 py-1 text-xs rounded ${
                            announcement.is_active 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {announcement.is_active ? 'Active' : 'Inactive'}
                        </button>
                        {announcement.expires_at && new Date(announcement.expires_at) > new Date() && (
                          <span className="px-2 py-1 text-xs rounded bg-blue-100 text-blue-800">
                            Expires: {new Date(announcement.expires_at).toLocaleDateString()}
                          </span>
                        )}
                        {announcement.expires_at && new Date(announcement.expires_at) <= new Date() && (
                          <span className="px-2 py-1 text-xs rounded bg-gray-100 text-gray-800">
                            Expired
                          </span>
                        )}
                      </div>
                      <p className="text-gray-600 whitespace-pre-wrap">{announcement.message}</p>
                      <p className="text-xs text-gray-400 mt-2">
                        Order: {announcement.display_order} | Created: {new Date(announcement.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <button
                        type="button"
                        onClick={() => setIsEditing(announcement.id)}
                        className="p-1 text-gray-500 hover:text-gray-700"
                        title="Edit"
                      >
                        <Edit2 className="w-5 h-5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteAnnouncement(announcement.id)}
                        className="p-1 text-red-500 hover:text-red-700"
                        title="Delete"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                  <div className="mt-4 flex gap-2">
                    {index > 0 && (
                      <button
                        type="button"
                        onClick={() => handleReorder(announcement.id, announcements[index - 1].display_order)}
                        className="flex items-center gap-1 px-3 py-1 text-sm text-gray-600 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
                      >
                        <ChevronUp className="w-4 h-4" />
                        Move Up
                      </button>
                    )}
                    {index < announcements.length - 1 && (
                      <button
                        type="button"
                        onClick={() => handleReorder(announcement.id, announcements[index + 1].display_order)}
                        className="flex items-center gap-1 px-3 py-1 text-sm text-gray-600 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
                      >
                        <ChevronDown className="w-4 h-4" />
                        Move Down
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

